import { useMediaQuery } from '@abyss/web/hooks/useMediaQuery';
import { IconMaterial } from '@abyss/web/ui/IconMaterial';
import { Text } from '@abyss/web/ui/Text';
import { Tooltip } from '@abyss/web/ui/Tooltip';
import { useContext } from 'react';

import { PortalContext } from '../../context/PortalContext';
import { useFeatureFlag } from '../../hooks/useFeatureFlag';
import { shouldUseGehaV2Styling } from '../../utils/gehaThemeUtils';
import { ConstantsLagoon } from '../ConstantsLagoon';
import { notXlScreenOnly, tabletScreen } from '../ConstantsStyles';
import { PortalIdentifier } from '../Portal';
import { truncateLargeStrings } from '../Utils/truncateLargeStrings';
import {
  HeadingWrapper,
  popupContainerDataCardTextStyles,
  providerNameDataCardTextStyles,
  providerNameListViewToolTipStyles,
  providerNameToolTipStyles,
} from './DataCard.style';

const nameheadingTextSize = (
  isGroupedLocationDrawer,
  groupedPinTextSize,
  expandedProviderListView,
  popupContainer,
  TabletScreen
) =>
  isGroupedLocationDrawer
    ? groupedPinTextSize
    : expandedProviderListView
    ? popupContainer
      ? '$sm'
      : TabletScreen
      ? '20.25px'
      : '25.63px'
    : popupContainer
    ? '$sm'
    : '$lg';

const GroupedPinNameHeading = (
  nameDataTestId,
  handleOnHeadingBlur,
  handleDetailsOnClick,
  handleHoverOnCard,
  name,
  sectionTypeDerived,
  groupedPinTextSize,
  hrefOptions,
  displayedName,
  _enableRightClickMenu,
  shouldWrapName,
  removeTruncatedDataCard
) => (
  <HeadingWrapper
    alignLayout="left"
    cssProps={{
      isGroupedLocationDrawer: true,
    }}
    data-auto-testid={nameDataTestId}
    data-testid={nameDataTestId}
    onBlur={handleOnHeadingBlur}
    onClick={(e) => {
      // Only prevent default and use programmatic navigation for left-clicks
      // Right-click menu will still work with the href
      if (e.button === 0) {
        e.preventDefault();
        handleDetailsOnClick?.({ showDirection: false });
      }
    }}
    onFocus={handleHoverOnCard}
  >
    <Tooltip
      asChild={false}
      content={name}
      css={providerNameListViewToolTipStyles(removeTruncatedDataCard)}
      position="top"
      positionOffset={8}
    >
      <Text
        color="$info1"
        css={providerNameDataCardTextStyles(shouldWrapName)}
        data-auto-testid={`data-card-detail-button-${sectionTypeDerived}`.replace(
          / /g,
          '-'
        )}
        data-testid={`data-card-detail-button-${sectionTypeDerived}`.replace(
          / /g,
          '-'
        )}
        fontWeight="$bold"
        size={groupedPinTextSize}
        {...hrefOptions}
      >
        {!removeTruncatedDataCard ? displayedName : name}
      </Text>
    </Tooltip>
  </HeadingWrapper>
);

type Props = {
  popupContainer;
  sectionTypeDerived;
  name;
  expandedProviderListView?;
  handleDetailsOnClick?;
  handleOnHeadingBlur;
  handleHoverOnCard;
  providerId;
  isNewMapViewDataCardDesign?: boolean;
  isGroupedLocationDrawer?;
  enableMapViewHideOption?: boolean;
  href: string;
  shouldWrapName?: boolean;
};

export const NameHeading = ({
  popupContainer,
  sectionTypeDerived,
  name,
  expandedProviderListView,
  handleDetailsOnClick,
  handleOnHeadingBlur,
  handleHoverOnCard,
  providerId,
  enableMapViewHideOption,
  href,
  isNewMapViewDataCardDesign = false,
  isGroupedLocationDrawer = false,
  shouldWrapName = false,
}: Props) => {
  const [enableRightClickMenu] = useFeatureFlag([
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_RIGHT_CLICK_MENU,
  ]);
  const [removeTruncatedDataCard] = useFeatureFlag([
    ConstantsLagoon.FEATURE_FLAGS.REMOVE_TRUNCATION_DATACARD,
  ]);

  const hrefOptions = enableRightClickMenu ? { href, as: 'a' } : {};
  const notXlScreen = useMediaQuery(notXlScreenOnly);
  const TabletScreen = useMediaQuery(tabletScreen);
  const displayedName = truncateLargeStrings(name, 18);

  const groupedPinTextSize = notXlScreen ? '16px' : '20.25px';
  const nameDataTestId = isGroupedLocationDrawer
    ? `data-card-heading-${sectionTypeDerived}-${providerId}`
    : `data-card-heading-${sectionTypeDerived}`;
  const { portalData } = useContext(PortalContext);
  const isGeha = portalData?.portalIdentifier === PortalIdentifier.GEHA;
  const isGEHAV2 = shouldUseGehaV2Styling();
  const isGehaTheme = isGEHAV2 || isGeha;

  return isGroupedLocationDrawer ? (
    GroupedPinNameHeading(
      nameDataTestId,
      handleOnHeadingBlur,
      handleDetailsOnClick,
      handleHoverOnCard,
      name,
      sectionTypeDerived,
      groupedPinTextSize,
      hrefOptions,
      displayedName,
      enableRightClickMenu,
      shouldWrapName,
      removeTruncatedDataCard
    )
  ) : (
    <HeadingWrapper
      alignLayout="left"
      css={
        popupContainer || isNewMapViewDataCardDesign
          ? {
              padding: '0px',
              justifyContent: 'center',
            }
          : ''
      }
      cssProps={{
        isGroupedLocationDrawer,
      }}
      data-auto-testid={nameDataTestId}
      data-testid={nameDataTestId}
      onBlur={handleOnHeadingBlur}
      onClick={(e) => {
        // Only prevent default and use programmatic navigation for left-clicks
        // Right-click menu will still work with the href
        if (e.button === 0) {
          e.preventDefault();
          handleDetailsOnClick?.({ showDirection: false });
        }
      }}
      onFocus={handleHoverOnCard}
    >
      <Tooltip
        content={name}
        css={
          popupContainer
            ? popupContainerDataCardTextStyles
            : expandedProviderListView
            ? providerNameListViewToolTipStyles
            : providerNameToolTipStyles
        }
        position="top"
        positionOffset={8}
      >
        <Text
          color="$info1"
          css={
            popupContainer
              ? popupContainerDataCardTextStyles
              : providerNameDataCardTextStyles(
                  enableMapViewHideOption,
                  shouldWrapName,
                  isGehaTheme,
                  removeTruncatedDataCard,
                  expandedProviderListView
                )
          }
          data-auto-testid={`data-card-detail-button-${sectionTypeDerived}`.replace(
            / /g,
            '-'
          )}
          data-testid={`data-card-detail-button-${sectionTypeDerived}`.replace(
            / /g,
            '-'
          )}
          fontWeight={enableMapViewHideOption ? '$semibold' : '$bold'}
          size={nameheadingTextSize(
            isGroupedLocationDrawer,
            groupedPinTextSize,
            expandedProviderListView,
            popupContainer,
            TabletScreen
          )}
          {...hrefOptions}
        >
          {popupContainer || !removeTruncatedDataCard ? displayedName : name}
        </Text>
      </Tooltip>
      {!expandedProviderListView &&
        !isNewMapViewDataCardDesign &&
        !popupContainer && (
          <IconMaterial
            className="arrow_forward"
            css={{
              width: '24px',
              height: '12px',
            }}
            data-auto-testid={`data-card-arrow-forward-icon-${sectionTypeDerived}-${providerId}`}
            data-testid={`data-card-arrow-forward-icon-${sectionTypeDerived}-${providerId}`}
            icon="arrow_forward_ios"
          />
        )}
    </HeadingWrapper>
  );
};
